import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Mic, Send, Volume2, Loader2, Keyboard, Sparkles, RotateCcw, ArrowRight, Bookmark } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { playTTS } from "@/lib/audio";
import { Waveform } from "@/components/Waveform";
import { useRecorder } from "@/hooks/useRecorder";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const withTimeout = (p, ms) => Promise.race([p, new Promise((r) => setTimeout(r, ms))]);

export default function Speaking() {
  const { sid } = useParams();
  const nav = useNavigate();
  const rec = useRecorder();
  const { profile } = useAuth();
  const { t } = useLang();
  const STATE_LABEL = { idle: t("sp_tap"), listening: t("sp_listening"), thinking: t("sp_thinking"), speaking: t("sp_ai_speaking") };

  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hints, setHints] = useState({});
  const [state, setState] = useState("idle");
  const [aiAnalyser, setAiAnalyser] = useState(null);
  const [textMode, setTextMode] = useState(false);
  const [draft, setDraft] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [flash, setFlash] = useState(false);
  const [pending, setPending] = useState(null);
  const startRef = useRef(Date.now());
  const scrollRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    client.post(`/conversations?sid=${sid}`).then(async ({ data }) => {
      if (!mounted) return;
      setConv(data.conversation);
      setMessages([data.opening]);
      startRef.current = Date.now();
      setState("speaking");
      const h = await playTTS(data.opening.text);
      setAiAnalyser(h.analyser);
      await withTimeout(h.done, 20000);
      if (mounted) { setAiAnalyser(null); setState("idle"); }
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/home"); });
    return () => { mounted = false; };
  }, [sid, nav]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, state]);

  const speak = async (text) => {
    setState("speaking");
    const h = await playTTS(text);
    setAiAnalyser(h.analyser);
    await withTimeout(h.done, 20000);
    setAiAnalyser(null);
    setState("idle");
  };

  const sendText = async (text) => {
    if (!text.trim() || !conv) return;
    setFlash(true); setTimeout(() => setFlash(false), 500);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text }]);
    setDraft("");
    setState("thinking");
    try {
      const { data } = await client.post(`/conversations/${conv.id}/message`, { text });
      setMessages((m) => [...m, data.reply]);
      await speak(data.reply.text);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setState("idle");
    }
  };

  const toggleMic = async () => {
    if (state === "listening") {
      const res = await rec.stop();
      setState("thinking");
      if (!res || !res.blob) { setState("idle"); return; }
      try {
        const fd = new FormData();
        fd.append("file", res.blob, res.filename);
        if (conv?.lang) fd.append("language", conv.lang);
        const { data } = await client.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const text = (data.text || "").trim();
        if (!text) { toast.error(t("didnt_catch")); setState("idle"); return; }
        if (profile?.voice_confirm) { setPending(text); setState("idle"); return; }
        await sendText(text);
      } catch { toast.error(t("couldnt_understand_audio")); setState("idle"); }
    } else if (state === "idle") {
      const ok = await rec.start();
      if (ok) setState("listening");
      else toast.error(t("mic_permission_kb"));
    }
  };

  const showSwiss = async (m) => {
    if (hints[m.id]) { setHints((h) => ({ ...h, [m.id]: { ...h[m.id], open: !h[m.id].open } })); return; }
    setHints((h) => ({ ...h, [m.id]: { loading: true, open: true } }));
    try {
      const { data } = await client.post("/swiss-hint", { text: m.text });
      setHints((h) => ({ ...h, [m.id]: { ...data, open: true } }));
    } catch { setHints((h) => { const n = { ...h }; delete n[m.id]; return n; }); toast.error(t("couldnt_load_swiss")); }
  };

  const finish = async () => {
    if (!conv) return;
    setAnalyzing(true);
    try {
      const dur = Math.round((Date.now() - startRef.current) / 1000);
      const { data } = await client.post(`/conversations/${conv.id}/complete`, { duration_seconds: dur });
      setAnalysis(data.analysis);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  if (analysis) return <AnalysisView analysis={analysis} onAgain={() => nav(0)} onNext={() => nav("/home")} />;

  const userTurns = messages.filter((m) => m.role === "user").length;
  const activeAnalyser = state === "listening" ? rec.analyser : state === "speaking" ? aiAnalyser : null;

  return (
    <div className="min-h-screen w-full bg-[#04040c] flex justify-center">
      <div className="relative w-full max-w-[430px] min-h-screen app-bg overflow-hidden flex flex-col">
        {flash && <div className="pointer-events-none absolute inset-0 z-30 animate-flash" style={{ background: "radial-gradient(circle at 50% 78%, rgba(37,99,235,0.35), transparent 60%)" }} />}
        <header className="flex items-center justify-between px-5 py-4 relative z-10">
          <button onClick={() => nav("/home")} data-testid="speaking-close" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 press"><X size={20} /></button>
          <div className="text-center">
            <p className="font-heading font-bold text-white">{conv?.situation_title || "Conversation"}</p>
            <p className="text-xs text-slate-400">{t("level")} {conv?.level} · {t("sp_roleplay")}</p>
          </div>
          <button onClick={finish} disabled={userTurns === 0 || analyzing} data-testid="speaking-finish" className="text-sm font-semibold text-cyan-300 disabled:text-slate-600">{t("finish")}</button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-5 space-y-3 pb-4 relative z-10">
          {messages.map((m) => (
            <div key={m.id} className={`flex animate-float-up ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 ${m.role === "user" ? "mel-gradient text-white rounded-br-md shadow-[0_10px_28px_-12px_rgba(37,99,235,0.8)]" : "glass-strong text-slate-100 rounded-bl-md"}`}>
                <p className="leading-relaxed">{m.text}</p>
                {m.role === "assistant" && (
                  <div className="mt-1.5 flex items-center gap-3">
                    <button onClick={() => playTTS(m.text)} className="flex items-center gap-1 text-xs text-cyan-300 font-medium" data-testid="msg-listen"><Volume2 size={13} /> {t("listen")}</button>
                    {conv?.lang === "de" && (conv?.switzerland || profile?.swiss_hints) && (
                      <button onClick={() => showSwiss(m)} className="flex items-center gap-1 text-xs text-red-300 font-medium" data-testid="msg-swiss">🇨🇭 Swiss German</button>
                    )}
                  </div>
                )}
                {m.role === "assistant" && hints[m.id]?.open && (
                  hints[m.id].loading ? (
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> …</p>
                  ) : (
                    <div className="mt-2 bg-red-500/10 border border-red-400/20 rounded-xl p-2.5" data-testid="swiss-box">
                      <p className="text-red-100 text-sm font-medium">{hints[m.id].swiss}</p>
                      {hints[m.id].note && <p className="text-slate-400 text-xs mt-1">{hints[m.id].note}</p>}
                      <button onClick={() => playTTS(hints[m.id].swiss, 0.92)} data-testid="swiss-listen"
                        className="mt-2 flex items-center gap-1 text-xs text-red-300 font-semibold press">
                        <Volume2 size={13} /> {t("listen_swiss")}
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
          {state === "thinking" && (
            <div className="flex justify-start"><div className="glass-strong rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2 text-slate-300"><Loader2 size={16} className="animate-spin" /> thinking…</div></div>
          )}
        </div>

        <div className="px-5 pb-8 pt-3 glass-strong border-t border-white/10 relative z-10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 24px)" }}>
          {pending ? (
            <div className="flex flex-col gap-3" data-testid="voice-confirm">
              <div className="glass-strong rounded-2xl px-4 py-3 text-center">
                <p className="text-xs text-slate-400 mb-1">{t("sp_confirm_q")}</p>
                <p className="text-[#0B1727] font-semibold">“{pending}”</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setDraft(pending); setTextMode(true); setPending(null); }} data-testid="vc-edit"
                  className="flex-1 glass py-3 rounded-2xl font-semibold text-slate-600 press">{t("edit")}</button>
                <button onClick={() => setPending(null)} data-testid="vc-rerecord"
                  className="flex-1 glass py-3 rounded-2xl font-semibold text-slate-600 press">{t("rerecord")}</button>
                <button onClick={() => { const x = pending; setPending(null); sendText(x); }} data-testid="vc-confirm"
                  className="flex-1 mel-gradient text-white py-3 rounded-2xl font-semibold press">{t("confirm")}</button>
              </div>
            </div>
          ) : textMode ? (
            <form onSubmit={(e) => { e.preventDefault(); sendText(draft); }} className="flex items-center gap-2">
              <input value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="speaking-text-input" placeholder={t("type_placeholder")}
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-3 outline-none text-white placeholder:text-slate-500" />
              <button type="submit" data-testid="speaking-send" className="w-12 h-12 rounded-full mel-gradient text-white flex items-center justify-center press"><Send size={18} /></button>
              <button type="button" onClick={() => setTextMode(false)} className="w-12 h-12 rounded-full bg-white/5 text-slate-300 flex items-center justify-center press"><Mic size={18} /></button>
            </form>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Waveform analyser={activeAnalyser} active={state === "listening" || state === "speaking"} bars={34} tone={state === "speaking" ? "cyan" : "violet"} className="w-full" />
              <p className="text-sm text-slate-400 font-medium h-4">{STATE_LABEL[state]}</p>
              <div className="flex items-center gap-6">
                <button onClick={() => setTextMode(true)} data-testid="speaking-keyboard" className="w-12 h-12 rounded-full bg-white/5 text-slate-300 flex items-center justify-center press"><Keyboard size={20} /></button>
                <button onClick={toggleMic} disabled={state === "thinking" || state === "speaking"} data-testid="speaking-mic"
                  className={`relative w-20 h-20 rounded-full flex items-center justify-center text-white press disabled:opacity-60 ${state === "listening" ? "bg-red-500" : "mel-gradient"} shadow-[0_12px_36px_-8px_rgba(37,99,235,0.7)]`}>
                  {state === "listening" && <span className="absolute inset-0 rounded-full bg-red-400 mic-ring-out" />}
                  <Mic size={30} className="relative" />
                </button>
                <div className="w-12 h-12" />
              </div>
            </div>
          )}
        </div>

        {analyzing && (
          <div className="absolute inset-0 z-40 bg-white/75 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
            <Loader2 className="animate-spin text-[#2563eb]" size={30} />
            <p className="text-[#0B1727] font-medium">{t("analyzing")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CountUp({ value = 0 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 900;
    const tick = (t) => { const p = Math.min(1, (t - start) / dur); setN(Math.round(value * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n}%</>;
}

function Score({ label, value }) {
  const v = Number.isFinite(value) ? value : 0;
  return (
    <div className="flex-1 text-center">
      <div className="relative w-16 h-16 mx-auto">
        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
          <path d="M18 2.5 a15.5 15.5 0 1 1 0 31 a15.5 15.5 0 1 1 0 -31" fill="none" stroke="rgba(11,23,39,0.07)" strokeWidth="3.5" />
          <path d="M18 2.5 a15.5 15.5 0 1 1 0 31 a15.5 15.5 0 1 1 0 -31" fill="none" stroke="url(#sc)" strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${v}, 100`} style={{ transition: "stroke-dasharray 1s ease" }} />
          <defs><linearGradient id="sc" x1="0" y1="0" x2="1" y2="1"><stop stopColor="#2563eb" /><stop offset="1" stopColor="#22d3ee" /></linearGradient></defs>
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-heading font-extrabold text-white text-sm"><CountUp value={v} /></span>
      </div>
      <p className="text-xs text-slate-400 mt-1 font-medium">{label}</p>
    </div>
  );
}

function AnalysisView({ analysis, onAgain, onNext }) {
  const a = analysis || {};
  const { t } = useLang();
  const savePhrase = async (better) => {
    try { await client.post("/phrases", { target_text: better, translation: a.summary || "", category: "Daily Life" }); toast.success(t("saved_to_phrases")); }
    catch { toast.error(t("couldnt_save")); }
  };
  return (
    <div className="min-h-screen w-full bg-[#04040c] flex justify-center">
      <div className="relative w-full max-w-[430px] min-h-screen app-bg flex flex-col px-5 py-8 overflow-y-auto no-scrollbar">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 animate-sweep" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.22), transparent)" }} />
        <div className="flex flex-col items-center text-center relative z-10 animate-float-up">
          <span className="w-16 h-16 rounded-full mel-gradient flex items-center justify-center text-white shadow-[0_12px_36px_-8px_rgba(37,99,235,0.8)]"><Sparkles size={28} /></span>
          <h1 className="font-heading text-2xl font-extrabold text-white mt-4">{t("convo_complete")}</h1>
          {a.level_estimate && <span className="mt-2 text-xs font-bold px-3 py-1 rounded-full bg-violet-500/15 text-violet-300">{t("level")} {a.level_estimate}</span>}
          <p className="text-slate-400 mt-2">{a.summary}</p>
        </div>
        <div className="flex gap-2 glass-strong rounded-3xl p-4 mt-6 relative z-10">
          <Score label={t("confidence")} value={a.speaking_confidence} />
          <Score label={t("pronunciation")} value={a.pronunciation} />
          <Score label={t("understanding")} value={a.understanding} />
        </div>

        {(a.highlight || a.focus) && (
          <div className="grid grid-cols-1 gap-3 mt-4 relative z-10">
            {a.highlight && (
              <div className="glass rounded-2xl p-4 border-l-4 border-emerald-400" data-testid="analysis-highlight">
                <p className="text-xs font-bold text-emerald-300 mb-1">✓ {t("did_well")}</p>
                <p className="text-slate-200 text-sm">{a.highlight}</p>
              </div>
            )}
            {a.focus && (
              <div className="glass rounded-2xl p-4 border-l-4 border-amber-400" data-testid="analysis-focus">
                <p className="text-xs font-bold text-amber-300 mb-1">◎ {t("focus_next")}</p>
                <p className="text-slate-200 text-sm">{a.focus}</p>
              </div>
            )}
          </div>
        )}

        {a.corrections?.length > 0 && (
          <>
            <h2 className="font-heading text-lg font-bold text-white mt-7 mb-3">{t("useful_corrections")}</h2>
            <div className="space-y-3">
              {a.corrections.map((c, i) => (
                <div key={i} className="glass rounded-2xl p-4" data-testid={`correction-${i}`}>
                  <p className="text-slate-500 line-through">❌ {c.said}</p>
                  <p className="text-white font-semibold mt-1">✅ {c.better}</p>
                  {c.note && <p className="text-sm text-slate-400 mt-1">{c.note}</p>}
                  <div className="flex gap-3 mt-3">
                    <button onClick={() => playTTS(c.better)} className="flex items-center gap-1 text-sm font-medium text-cyan-300" data-testid={`correction-listen-${i}`}><Volume2 size={15} /> {t("listen")}</button>
                    <button onClick={() => savePhrase(c.better)} className="flex items-center gap-1 text-sm font-medium text-slate-400"><Bookmark size={15} /> {t("save")}</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {a.vocab?.length > 0 && (
          <>
            <h2 className="font-heading text-lg font-bold text-white mt-7 mb-3">{t("new_words")}</h2>
            <div className="flex flex-wrap gap-2">
              {a.vocab.map((v, i) => (<span key={i} className="glass rounded-full px-3 py-1.5 text-sm text-slate-200"><b>{v.word}</b> — {v.translation}</span>))}
            </div>
          </>
        )}

        <div className="mt-8 space-y-3">
          <button onClick={onAgain} data-testid="practice-again" className="w-full glass text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 press"><RotateCcw size={18} /> {t("practice_again")}</button>
          <button onClick={onNext} data-testid="next-situation" className="w-full mel-gradient text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_14px_34px_-14px_rgba(37,99,235,0.8)] press">{t("next_situation")} <ArrowRight size={18} /></button>
        </div>
      </div>
    </div>
  );
}
